<!-- eslint-disable vue/no-deprecated-v-bind-sync -->
<template>
    <vue-scroll>
        <div class="checkout-container">
            <div class="checkout-grid">
                <!-- Order Summary Section -->
                <div class="checkout-summary">
                    <h2>Order Summary</h2>
                    <div class="summary-card">
                        <cart
                            ref="cart"
                            :show-products="true"
                            :with-header="false"
                            :show-product-delete-buttons="true"
                            :rush-processing.sync="orderForm.rush_processing"
                            :custom-shipping="orderForm.custom_shipping"
                            @loading-shipping="onLoadingShipping"
                            @compute-shipping="onComputeShipping"
                            @grand-total-updated="onGrandTotalUpdated"
                            @shipping-loaded="onShippingLoaded"
                        />
                    </div>
                </div>

                <!-- Checkout Form Section -->
                <div class="checkout-form">
                    <h2>Checkout Details</h2>
                    <div class="form-card" v-loading="loading">
                        <template v-if="isSuspended">
                            <el-alert
                                title="Please contact Becky in the Business Office at becky@corwindesign.com to discuss an overdue invoice. Thank you - we appreciate your business!"
                                type="error"
                                :closable="false"
                                center
                            >
                            </el-alert>
                        </template>
                        <template v-else>
                            <el-form
                                class="mb-20"
                                v-if="cart.orders.length > 0"
                            >
                                <el-form>
                                    <!--Rush Processing-->
                                    <el-form-item
                                        class="mb-0"
                                        v-if="cartEditable"
                                    >
                                        <el-checkbox
                                            @change="onChangeRushProcessing"
                                            v-model="orderForm.rush_processing"
                                        >
                                            Rush Processing ({{
                                                rushProcessingFee | currency
                                            }})
                                            <el-tooltip
                                                class="item"
                                                effect="dark"
                                                placement="top"
                                            >
                                                <div
                                                    class="text-center"
                                                    slot="content"
                                                >
                                                    Get your order to the front
                                                    of the line! Checking this
                                                    gets your order<br />
                                                    through the office quickly.
                                                    It does not include rush
                                                    shipping
                                                </div>
                                                <a @click.prevent href="#">
                                                    <i
                                                        class="mdi mdi-help-circle"
                                                    ></i>
                                                </a>
                                            </el-tooltip>
                                        </el-checkbox>
                                    </el-form-item>

                                    <template v-if="customShippingEnabled">
                                        <el-form-item
                                            class="mb-0"
                                            v-if="cartEditable"
                                        >
                                            <el-checkbox
                                                @change="onChangeCustomShipping"
                                                v-model="
                                                    orderForm.custom_shipping
                                                "
                                            >
                                                Use Custom UPS/FedEx Account
                                                Number for Shipping
                                                <el-tooltip
                                                    class="item"
                                                    effect="dark"
                                                    placement="top"
                                                >
                                                    <div
                                                        class="text-center"
                                                        slot="content"
                                                    >
                                                        Enter your company's UPS
                                                        or FedEx number and we
                                                        can bill that<br />
                                                        account directly for
                                                        next-day shipping
                                                    </div>
                                                    <a @click.prevent href="#">
                                                        <i
                                                            class="mdi mdi-help-circle"
                                                        ></i>
                                                    </a>
                                                </el-tooltip>
                                            </el-checkbox>
                                        </el-form-item>

                                        <template
                                            v-if="orderForm.custom_shipping"
                                        >
                                            <el-form-item
                                                :class="
                                                    orderForm.state(
                                                        'custom_shipping_provider'
                                                    )
                                                "
                                                class="mb-0"
                                                v-if="cartEditable"
                                            >
                                                <el-radio-group
                                                    data-vv-as="provider"
                                                    name="custom_shipping_provider"
                                                    v-model="
                                                        orderForm.custom_shipping_provider
                                                    "
                                                    v-validate="'required'"
                                                >
                                                    <el-radio
                                                        :label="
                                                            ShippingProviders.UPS
                                                        "
                                                        >UPS</el-radio
                                                    >
                                                    <el-radio
                                                        :label="
                                                            ShippingProviders.FEDEX
                                                        "
                                                        >FedEx</el-radio
                                                    >
                                                </el-radio-group>
                                                <form-feedback
                                                    :form="orderForm"
                                                    custom-message="Please select a shipping provider."
                                                    field="custom_shipping_provider"
                                                />
                                            </el-form-item>
                                            <el-form-item
                                                :class="
                                                    orderForm.state(
                                                        'custom_shipping_account_number'
                                                    )
                                                "
                                                class="mb-0"
                                                label="Account Number"
                                            >
                                                <el-input
                                                    :maxlength="
                                                        orderForm.custom_shipping_provider ===
                                                        ShippingProviders.UPS
                                                            ? 6
                                                            : 9
                                                    "
                                                    data-vv-as="account number"
                                                    name="custom_shipping_account_number"
                                                    v-model="
                                                        orderForm.custom_shipping_account_number
                                                    "
                                                    v-validate="'required'"
                                                />
                                                <form-feedback
                                                    :form="orderForm"
                                                    field="custom_shipping_account_number"
                                                />
                                            </el-form-item>
                                        </template>
                                    </template>

                                    <!--Shipping Address-->
                                    <h4 class="mb-0">Shipping Address:</h4>
                                    <span
                                        class="mt-0"
                                        v-if="!orderForm.different_shipping"
                                        v-html="formattedAddress"
                                    >
                                    </span>
                                    <el-form-item class="mb-0">
                                        <el-checkbox
                                            @change="
                                                onChangeDifferentShippingAddressCheckbox
                                            "
                                            v-model="
                                                orderForm.different_shipping
                                            "
                                        >
                                            Use Different Shipping Address
                                        </el-checkbox>
                                    </el-form-item>
                                    <template
                                        v-if="orderForm.different_shipping"
                                    >
                                        <el-row :gutter="20">
                                            <el-col :md="12">
                                                <el-form-item
                                                    label="Street"
                                                    :class="
                                                        shippingForm.state(
                                                            'shipping_address.street'
                                                        )
                                                    "
                                                >
                                                    <el-input
                                                        name="street"
                                                        v-model="
                                                            shippingForm
                                                                .shipping_address
                                                                .street
                                                        "
                                                    />
                                                    <form-feedback
                                                        :form="shippingForm"
                                                        field="shipping_address.street"
                                                    />
                                                </el-form-item>
                                            </el-col>
                                            <el-col :md="12">
                                                <el-form-item
                                                    :class="
                                                        shippingForm.state(
                                                            'shipping_address.city'
                                                        )
                                                    "
                                                    label="City"
                                                >
                                                    <el-input
                                                        name="city"
                                                        v-model="
                                                            shippingForm
                                                                .shipping_address
                                                                .city
                                                        "
                                                    />
                                                    <form-feedback
                                                        :form="shippingForm"
                                                        field="shipping_address.city"
                                                    />
                                                </el-form-item>
                                            </el-col>
                                        </el-row>
                                        <el-row :gutter="20">
                                            <el-col :md="12">
                                                <el-form-item
                                                    :class="
                                                        shippingForm.state(
                                                            'shipping_address.state'
                                                        )
                                                    "
                                                    label="State"
                                                >
                                                    <state-select
                                                        name="state"
                                                        v-model="
                                                            shippingForm
                                                                .shipping_address
                                                                .state
                                                        "
                                                    />
                                                    <form-feedback
                                                        :form="shippingForm"
                                                        field="shipping_address.state"
                                                    />
                                                </el-form-item>
                                            </el-col>
                                            <el-col :md="12">
                                                <el-form-item
                                                    :class="
                                                        shippingForm.state(
                                                            'shipping_address.zip'
                                                        )
                                                    "
                                                    label="Zip"
                                                >
                                                    <el-input
                                                        name="zip"
                                                        v-model="
                                                            shippingForm
                                                                .shipping_address
                                                                .zip
                                                        "
                                                    />
                                                    <form-feedback
                                                        :form="shippingForm"
                                                        field="shipping_address.zip"
                                                    />
                                                </el-form-item>
                                            </el-col>
                                        </el-row>
                                        <el-button
                                            :loading="shippingForm.busy"
                                            @click="saveShippingAddress()"
                                            icon="mdi mdi-content-save"
                                            type="primary"
                                            :disabled="
                                                !shippingFormChanged ||
                                                shippingForm.busy
                                            "
                                        >
                                            Save
                                        </el-button>
                                    </template>

                                    <!--Billing Address-->
                                    <h4 class="mb-0">Billing Address:</h4>
                                    <el-form-item class="mb-0">
                                        <el-checkbox
                                            @change="
                                                onChangeBillingSameAsShipping
                                            "
                                            v-model="
                                                orderForm.billing_same_as_shipping
                                            "
                                        >
                                            Same as Shipping Address
                                        </el-checkbox>
                                    </el-form-item>
                                    <template
                                        v-if="
                                            !orderForm.billing_same_as_shipping
                                        "
                                    >
                                        <el-row :gutter="20">
                                            <el-col :md="12">
                                                <el-form-item
                                                    :class="
                                                        billingForm.state(
                                                            'billing_address.street'
                                                        )
                                                    "
                                                    label="Street"
                                                >
                                                    <el-input
                                                        name="billing_street"
                                                        v-model="
                                                            billingForm
                                                                .billing_address
                                                                .street
                                                        "
                                                    />
                                                    <form-feedback
                                                        :form="billingForm"
                                                        field="billing_address.street"
                                                    />
                                                </el-form-item>
                                            </el-col>
                                            <el-col :md="12">
                                                <el-form-item
                                                    :class="
                                                        billingForm.state(
                                                            'billing_address.city'
                                                        )
                                                    "
                                                    label="City"
                                                >
                                                    <el-input
                                                        name="billing_city"
                                                        v-model="
                                                            billingForm
                                                                .billing_address
                                                                .city
                                                        "
                                                    />
                                                    <form-feedback
                                                        :form="billingForm"
                                                        field="billing_address.city"
                                                    />
                                                </el-form-item>
                                            </el-col>
                                        </el-row>
                                        <el-row :gutter="20">
                                            <el-col :md="12">
                                                <el-form-item
                                                    :class="
                                                        billingForm.state(
                                                            'billing_address.state'
                                                        )
                                                    "
                                                    label="State"
                                                >
                                                    <state-select
                                                        name="billing_state"
                                                        v-model="
                                                            billingForm
                                                                .billing_address
                                                                .state
                                                        "
                                                    />
                                                    <form-feedback
                                                        :form="billingForm"
                                                        field="billing_address.state"
                                                    />
                                                </el-form-item>
                                            </el-col>
                                            <el-col :md="12">
                                                <el-form-item
                                                    :class="
                                                        billingForm.state(
                                                            'billing_address.zip'
                                                        )
                                                    "
                                                    label="Zip"
                                                >
                                                    <el-input
                                                        name="billing_zip"
                                                        v-model="
                                                            billingForm
                                                                .billing_address
                                                                .zip
                                                        "
                                                    />
                                                    <form-feedback
                                                        :form="billingForm"
                                                        field="billing_address.zip"
                                                    />
                                                </el-form-item>
                                            </el-col>
                                        </el-row>
                                        <el-button
                                            :loading="billingForm.busy"
                                            @click="saveBillingAddress()"
                                            icon="mdi mdi-content-save"
                                            type="primary"
                                            :disabled="
                                                !billingFormChanged ||
                                                billingForm.busy
                                            "
                                        >
                                            Save
                                        </el-button>
                                    </template>
                                </el-form>

                                <!--Credit Card-->
                                <template v-if="cartEditable">
                                    <h4 class="mb-10">Payment Options:</h4>
                                    <el-form-item
                                        v-if="
                                            showPaymentType ===
                                                PaymentTypes.IMMEDIATE ||
                                            showPaymentType ===
                                                PaymentTypes.BOTH_OPTIONS ||
                                            showPaymentType ===
                                                PaymentTypes.BILL_TO_ACCOUNT
                                        "
                                    >
                                        <el-radio-group
                                            v-model="orderForm.payment_type"
                                        >
                                            <el-radio
                                                :label="
                                                    PaymentTypes.BILL_TO_ACCOUNT
                                                "
                                                :disabled="
                                                    showPaymentType ===
                                                    PaymentTypes.IMMEDIATE
                                                "
                                            >
                                                Bill Me
                                            </el-radio>
                                            <el-radio
                                                :label="PaymentTypes.IMMEDIATE"
                                                :disabled="
                                                    showPaymentType ===
                                                        PaymentTypes.BILL_TO_ACCOUNT ||
                                                    (shipping_rate === null &&
                                                        !orderForm.custom_shipping &&
                                                        shippingLoaded)
                                                "
                                            >
                                                Credit Card
                                            </el-radio>
                                        </el-radio-group>
                                        <div
                                            v-if="
                                                shipping_rate === null &&
                                                !orderForm.custom_shipping &&
                                                shippingLoaded
                                            "
                                            class="payment-notice"
                                        >
                                            <i
                                                class="el-icon-warning-outline"
                                            ></i>
                                            Credit card payment unavailable
                                            until shipping is determined.
                                        </div>
                                    </el-form-item>

                                    <!-- Credit Card Section -->
                                    <div
                                        class="ml-25"
                                        v-if="
                                            orderForm.payment_type ===
                                            PaymentTypes.IMMEDIATE
                                        "
                                    >
                                        <el-form-item
                                            class="mb-10"
                                            v-if="hasExistingCard"
                                        >
                                            <el-radio
                                                :label="true"
                                                v-model="orderForm.use_existing"
                                                >Use Existing Card ({{
                                                    current_community.card_brand
                                                }}
                                                **** **** ****
                                                {{
                                                    current_community.pm_last_four
                                                }})
                                            </el-radio>
                                            <br />
                                            <el-radio
                                                :label="false"
                                                v-model="orderForm.use_existing"
                                                >Use Different Card
                                            </el-radio>
                                            <form-feedback
                                                :form="orderForm"
                                                field="payment_method"
                                            />
                                        </el-form-item>
                                        <el-form-item
                                            class="mb-0"
                                            v-if="!orderForm.use_existing"
                                        >
                                            <StripeElements
                                                ref="stripeElements"
                                                @change="handleStripeChange"
                                            />
                                        </el-form-item>
                                        <el-form-item
                                            class="mb-0"
                                            v-if="!orderForm.use_existing"
                                        >
                                            <el-checkbox
                                                v-model="orderForm.save_card"
                                                >Save Credit Card for Future
                                                Payments
                                            </el-checkbox>
                                        </el-form-item>
                                    </div>

                                    <!-- Bill Me Section -->
                                    <p
                                        class="bill-to ml-25"
                                        v-if="
                                            orderForm.payment_type ===
                                            PaymentTypes.BILL_TO_ACCOUNT
                                        "
                                    >
                                        {{ grandTotal | currency }} will be
                                        billed to your community.
                                    </p>
                                </template>
                                <template v-else>
                                    <p class="bill-to">
                                        {{ grandTotal | currency }} will be
                                        billed to your community.
                                    </p>
                                </template>
                                <el-form-item
                                    :class="orderForm.state('po_number')"
                                    label="PO Number"
                                    required
                                    v-if="requiresPoNumber"
                                >
                                    <el-input
                                        data-vv-as="PO number"
                                        name="po_number"
                                        v-model="orderForm.po_number"
                                        v-validate="'required'"
                                    />
                                    <form-feedback
                                        :form="orderForm"
                                        field="po_number"
                                    />
                                </el-form-item>
                                <el-form-item
                                    :class="orderForm.state('notes')"
                                    label="Notes to Corwin"
                                >
                                    <el-input
                                        :autosize="{ minRows: 3, maxRows: 6 }"
                                        name="notes"
                                        type="textarea"
                                        v-model="orderForm.notes"
                                    />
                                    <form-feedback
                                        :form="orderForm"
                                        field="notes"
                                    />
                                </el-form-item>
                                <el-form-item
                                    v-if="
                                        !hasMissingCalendar &&
                                        !hasMissingNewsletter
                                    "
                                >
                                    <el-button
                                        type="primary"
                                        @click="submit"
                                        :disabled="
                                            (paymentType ===
                                                PaymentTypes.IMMEDIATE &&
                                                !complete &&
                                                !orderForm.use_existing) ||
                                            shippingForm.busy ||
                                            loadingShipping ||
                                            loadingTax ||
                                            detailsForm.busy ||
                                            orderForm.busy
                                        "
                                        :loading="orderForm.busy"
                                        :icon="
                                            loadingShipping
                                                ? 'el-icon-loading'
                                                : 'mdi mdi-check-outline'
                                        "
                                    >
                                        <span
                                            v-if="
                                                shippingForm.busy ||
                                                loadingShipping
                                            "
                                            >Calculating Shipping Fee</span
                                        >
                                        <span
                                            v-else-if="
                                                loadingTax || detailsForm.busy
                                            "
                                            >Saving Changes...</span
                                        >
                                        <span v-else>Submit Order</span>
                                    </el-button>
                                    <p>
                                        <el-alert
                                            class="submit-alert"
                                            type="info"
                                            :closable="false"
                                            >Please be sure to proofread your
                                            content before clicking Submit.
                                            <br />Any changes made to your
                                            content after submitting your order
                                            are billable.</el-alert
                                        >
                                    </p>
                                </el-form-item>
                            </el-form>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </vue-scroll>
</template>

<script>
import * as PaymentTypes from "@/constants/payment-types";
import * as Routes from "@/constants/routes";
import * as ShippingProviders from "@/constants/shipping-providers";
import address from "@/mixins/address";
import auth from "@/mixins/auth";
import cart_mixin from "@/mixins/cart_mixin";
import * as Actions from "@/store/constants/actions";
import * as Mutations from "@/store/constants/mutations";
import store from "@/store/index";
import axios from "axios";
import _ from "lodash";
import Swal from "sweetalert2";
import StripeElements from "@/components/StripeElementsPlaceholder";
import { mapState } from "vuex";
import Cart from "../calendar/single/place-order/Cart";

let address_fields = ["street", "city", "state", "zip"];

export default {
    mixins: [auth, address, cart_mixin],
    components: {
        StripeElements,
        Cart
    },
    beforeRouteEnter(to, from, next) {
        store.dispatch(Actions.FETCH_CART).then(() => {
            next();
        });
    },
    data() {
        return {
            PaymentTypes,
            ShippingProviders,
            complete: false,
            shipping_rate: null,
            grandTotal: null,
            detailsForm: new window.Form({
                rush_processing: false
            }),
            shippingForm: new window.Form(
                {
                    different_shipping: false,
                    shipping_address: {
                        street: null,
                        city: null,
                        state: null,
                        zip: null
                    }
                },
                null,
                null,
                {
                    different_shipping: "boolean",
                    "shipping_address.street": "required|string|max:255",
                    "shipping_address.city": "required|string|max:255",
                    "shipping_address.state": "required|string|max:255",
                    "shipping_address.zip": "required|string|max:255"
                },
                {
                    "shipping_address.street": "street",
                    "shipping_address.city": "city",
                    "shipping_address.state": "state",
                    "shipping_address.zip": "zip"
                },
                {
                    required_if: "The :attribute field is required."
                }
            ),
            billingForm: new window.Form(
                {
                    billing_same_as_shipping: false,
                    billing_address: {
                        street: null,
                        city: null,
                        state: null,
                        zip: null
                    }
                },
                null,
                null,
                {
                    billing_same_as_shipping: "boolean",
                    "billing_address.street": "required|string|max:255",
                    "billing_address.city": "required|string|max:255",
                    "billing_address.state": "required|string|max:255",
                    "billing_address.zip": "required|string|max:255"
                },
                {
                    "billing_address.street": "street",
                    "billing_address.city": "city",
                    "billing_address.state": "state",
                    "billing_address.zip": "zip"
                },
                {
                    required_if: "The :attribute field is required"
                }
            ),
            showPaymentType: null,
            stripeLoaded: false,
            loading: false,
            loadingShipping: false,
            shippingLoaded: false,
            shippingFormChanged: false,
            billingFormChanged: false,
            shippingFormInitialValues: null,
            billingFormInitialValues: null
        };
    },
    computed: {
        ...mapState(["current_community", "current_order"]),
        stripeKey() {
            return this.$store.state.config.services.stripe.key;
        },
        amount() {
            return _.sumBy(this.cart.orders, (item) => {
                if (item.calendar_id) {
                    return parseFloat(item.total_amount);
                }
                return 0;
            });
        },
        totalAmount() {
            return (
                this.amount +
                _.get(this.shipping_rate, "charge", 0) +
                (this.orderForm.rush_processing ? this.rushProcessingFee : 0)
            );
        },
        hasExistingCard() {
            return this.current_community.has_card;
        },
        cart() {
            return this.$store.state.cart;
        },
        paymentType() {
            return this.orderForm.payment_type;
        },
        isSuspended() {
            return this.$store.state.current_community.suspended;
        },
        rushProcessingFee() {
            return this.$store.state.options.rush_processing_fee;
        },
        address() {
            return this.getAddressFullText(this.current_community.address);
        },
        formattedAddress() {
            return this.getFormattedAddress(this.current_community.address);
        },
        requiresPoNumber() {
            return _.get(
                this.current_community,
                "corporation.requires_po_number",
                false
            );
        },
        customShippingEnabled() {
            return !this.current_community.disable_custom_shipping;
        },
        isStripeLoaded() {
            return this.stripeLoaded;
        },
        stripeElementOptions() {
            // Get the appropriate ZIP code
            const billingZip = this.billingForm.billing_same_as_shipping
                ? this.shippingForm.different_shipping
                    ? this.shippingForm.shipping_address.zip
                    : this.current_community.address.zip
                : this.billingForm.billing_address.zip;

            return {
                style: {
                    base: {
                        fontSize: "16px",
                        color: "#424770",
                        "::placeholder": {
                            color: "#aab7c4"
                        }
                    },
                    invalid: {
                        color: "#9e2146"
                    }
                },
                classes: {
                    focus: "focused",
                    empty: "empty",
                    invalid: "invalid"
                },
                hidePostalCode: true,
                value: {
                    postalCode: billingZip
                }
            };
        },
        hasMissingCalendar() {
            return false; // Placeholder - implement based on your business logic
        },
        hasMissingNewsletter() {
            return false; // Placeholder - implement based on your business logic
        }
    },
    mounted() {
        this.$loadScript("https://js.stripe.com/v3/")
            .then(() => {
                // Script is loaded
                this.stripeLoaded = true;
            })
            .catch((e) => {
                console.log(e);
                // Failed to fetch script
                this.stripeLoaded = false;
            });
        this.orderForm.setVeeErrors(this.veeErrors);
        this.showPaymentType = this.current_community.payment_type;
        if (this.hasExistingCard) {
            this.orderForm.use_existing = true;
        }
        if (this.current_community.payment_type === PaymentTypes.IMMEDIATE) {
            this.orderForm.payment_type = PaymentTypes.IMMEDIATE;
        }
        if (
            this.current_community.payment_type === PaymentTypes.BILL_TO_ACCOUNT
        ) {
            this.orderForm.payment_type = PaymentTypes.BILL_TO_ACCOUNT;
        }
        if (this.current_community.payment_type === PaymentTypes.BOTH_OPTIONS) {
            if (this.current_community.has_previous_order) {
                if (
                    this.current_community.previous_order.payment_type ===
                    PaymentTypes.IMMEDIATE
                ) {
                    this.orderForm.payment_type = PaymentTypes.IMMEDIATE;
                } else {
                    this.orderForm.payment_type = PaymentTypes.BILL_TO_ACCOUNT;
                }
            } else {
                this.orderForm.payment_type = PaymentTypes.BILL_TO_ACCOUNT;
            }
        }
        this.$store.dispatch(Actions.FETCH_CURRENT_ORDER).then((response) => {
            this.shipping_rate = response.data.shipping_rate;
            this.orderForm.shipping_rate_id = _.get(this.shipping_rate, "id");
        });
        this.initForms();
        this.getCustomShippingProvider(true);
    },
    watch: {
        "orderForm.different_shipping"(value) {
            this.shippingForm.different_shipping = value;
        },
        "orderForm.billing_same_as_shipping"(value) {
            this.billingForm.billing_same_as_shipping = value;
        },
        "orderForm.payment_type": function (newVal) {
            if (newVal === PaymentTypes.BILL_TO_ACCOUNT) {
                this.complete = false;
                this.orderForm.errors.remove("card_token");
                if (this.$refs.card && this.$refs.card.stripeElement) {
                    this.$refs.card.stripeElement.clear();
                }
            }
        },
        // Watch for shipping address changes
        "shippingForm.shipping_address": {
            handler(newVal) {
                this.shippingFormChanged = this.checkShippingFormChanged();
            },
            deep: true
        },

        // Watch for billing address changes
        "billingForm.billing_address": {
            handler(newVal) {
                this.billingFormChanged = this.checkBillingFormChanged();
            },
            deep: true
        },

        // Reset shipping form when different_shipping changes
        "shippingForm.different_shipping"(newVal) {
            if (!newVal) {
                this.shippingFormChanged = false;
            }
        },

        // Reset billing form when billing_same_as_shipping changes
        "billingForm.billing_same_as_shipping"(newVal) {
            if (newVal) {
                this.billingFormChanged = false;
            }
        }
    },
    methods: {
        initForms() {
            this.detailsForm.rush_processing =
                this.$store.state.cart.rush_processing;
            _.extend(
                this.orderForm,
                _.pick(this.$store.state.cart, [
                    "rush_processing",
                    "different_shipping",
                    "billing_same_as_shipping"
                ])
            );

            this.shippingForm.different_shipping =
                this.$store.state.cart.different_shipping;
            _.extend(
                this.shippingForm.shipping_address,
                _.pick(this.$store.state.cart.shipping_address, address_fields)
            );

            this.billingForm.billing_same_as_shipping =
                this.$store.state.cart.billing_same_as_shipping;
            _.extend(
                this.billingForm.billing_address,
                _.pick(this.$store.state.cart.billing_address, address_fields)
            );

            // Store initial form values
            this.shippingFormInitialValues = _.cloneDeep(
                this.shippingForm.shipping_address
            );
            this.billingFormInitialValues = _.cloneDeep(
                this.billingForm.billing_address
            );

            // Reset change flags
            this.shippingFormChanged = false;
            this.billingFormChanged = false;
        },
        async submit() {
            try {
                // Validate form
                const isValid = await this.$validator.validate();
                if (!isValid) {
                    return;
                }

                // Confirm order submission
                let confirmed;
                try {
                    confirmed = await this.$confirm(
                        `I have proofed my order & it's ready to print.`,
                        "Confirmation",
                        {
                            cancelButtonText: "No",
                            confirmButtonText: "Yes",
                            type: "info"
                        }
                    );
                } catch (e) {
                    // User cancelled confirmation dialog, do nothing
                    return;
                }

                if (!confirmed) {
                    return;
                }

                // Handle credit card payment if needed
                if (
                    this.paymentType === PaymentTypes.IMMEDIATE &&
                    this.cartEditable
                ) {
                    if (!this.orderForm.use_existing) {
                        const cardToken = await this.createCardToken();
                        if (!cardToken) {
                            return;
                        }
                        this.orderForm.card_token = cardToken;
                    } else {
                        this.orderForm.card_token = null;
                    }
                }

                // Prepare order submission
                this.orderForm.coupon = this.$store.state.active_coupon;
                this.orderForm.startProcessing();

                // Submit order
                await window.App.post("/web-api/cart/checkout", this.orderForm);

                // Handle success
                await Swal.fire({
                    type: "success",
                    title: "Thank you!",
                    text: "Order has been submitted."
                });

                // Update store and redirect
                this.$router.push({ name: Routes.DASHBOARD });
                this.$store.commit(Mutations.SET_CART, { orders: [] });
                await this.$store.dispatch(Actions.FETCH_CURRENT_COMMUNITY);
            } catch (error) {
                // Only show error if not a cancel from $confirm
                if (error && error !== false && error !== "cancel") {
                    console.error("Checkout error:", error);
                    this.orderForm.finishProcessing();

                    Swal.fire({
                        type: "error",
                        title: "Checkout Failed",
                        text:
                            error.response?.data?.message ||
                            "There was an error processing your order."
                    });
                }
            }
        },

        async createCardToken() {
            try {
                const stripeElements = this.$refs.stripeElements;

                if (!stripeElements) {
                    throw new Error("Stripe elements component not found");
                }

                // Get billing details
                const billingDetails = this.getBillingDetails();

                // Create payment method using our Stripe Elements component with billing details
                const paymentMethod = await stripeElements.createPaymentMethod({
                    name: this.current_community.name,
                    phone: this.current_community.phone_number,
                    address: {
                        city: billingDetails.city,
                        state: billingDetails.state,
                        line1: billingDetails.line1,
                        line2: billingDetails.line2,
                        postal_code: billingDetails.postal_code,
                        country: "us"
                    }
                });

                return paymentMethod.id;
            } catch (error) {
                console.error("Payment method creation error:", error);
                this.orderForm.errors.add(
                    "card_token",
                    error.message || "Failed to process card. Please try again."
                );
                return null;
            }
        },

        getBillingDetails() {
            const useBillingAddress =
                !this.billingForm.billing_same_as_shipping;
            const useShippingAddress = this.shippingForm.different_shipping;
            const communityAddress = this.current_community.address;

            const getAddress = (type) => {
                if (type === "billing" && useBillingAddress) {
                    return this.billingForm.billing_address;
                }
                if (type === "shipping" && useShippingAddress) {
                    return this.shippingForm.shipping_address;
                }
                return communityAddress;
            };

            const address = useBillingAddress
                ? getAddress("billing")
                : getAddress("shipping");

            return {
                city: address.city,
                state: address.state,
                line1: address.street,
                line2: address.street_2 || "",
                postal_code: address.zip
            };
        },
        computeShipping() {
            if (this.$refs.cart) {
                return this.$refs.cart.computeShipping();
            }
        },
        onComputeShipping(shipping_rate) {
            this.shipping_rate = shipping_rate;
            this.orderForm.shipping_rate_id = shipping_rate
                ? shipping_rate.id
                : null;
        },
        onLoadingShipping(loading) {
            this.loadingShipping = loading;
        },
        onShippingLoaded(loaded) {
            this.shippingLoaded = loaded;
        },
        onGrandTotalUpdated(grandTotal) {
            this.grandTotal = grandTotal;
        },
        onChangeCustomShipping() {
            if (!this.orderForm.custom_shipping) {
                this.orderForm.custom_shipping_provider = null;
                this.orderForm.custom_shipping_account_number = null;
                this.orderForm.payment_type = PaymentTypes.BILL_TO_ACCOUNT;
            } else {
                this.getCustomShippingProvider(false);
                this.shipping_rate = null;
                this.orderForm.shipping_rate_id = null;
            }
        },
        onChangeDifferentShippingAddressCheckbox(val) {
            this.shippingForm.different_shipping = val;
            if (!val) {
                this.saveShippingAddress();
            }
        },
        onChangeBillingSameAsShipping(val) {
            this.billingForm.billing_same_as_shipping = val;
            if (val) {
                this.saveBillingAddress();
            }
        },
        onChangeRushProcessing(val) {
            this.detailsForm.rush_processing = val;
            axios
                .put(`/web-api/cart/details`, this.detailsForm)
                .then(() => {
                    this.$store.dispatch(Actions.FETCH_CART_TAX);
                })
                .catch((e) => {
                    console.log(e);
                });
        },
        saveShippingAddress() {
            if (
                !this.shippingForm.different_shipping ||
                this.shippingForm.validate()
            ) {
                this.shippingForm.busy = true;
                axios
                    .put(`/web-api/cart/details`, this.shippingForm)
                    .then(() => {
                        this.$notify.success({
                            message: "Shipping information updated."
                        });
                        this.computeShipping();

                        // Update initial values and reset changed flag
                        this.shippingFormInitialValues = _.cloneDeep(
                            this.shippingForm.shipping_address
                        );
                        this.shippingFormChanged = false;
                    })
                    .catch((e) => {
                        console.log(e);
                    })
                    .finally(() => {
                        this.shippingForm.busy = false;
                    });
            }
        },
        saveBillingAddress() {
            if (
                this.billingForm.billing_same_as_shipping ||
                this.billingForm.validate()
            ) {
                this.billingForm.busy = true;
                axios
                    .put(`/web-api/cart/details`, this.billingForm)
                    .then(() => {
                        this.$notify.success({
                            message: "Billing information updated."
                        });

                        // Update initial values and reset changed flag
                        this.billingFormInitialValues = _.cloneDeep(
                            this.billingForm.billing_address
                        );
                        this.billingFormChanged = false;
                    })
                    .catch((e) => {
                        console.log(e);
                    })
                    .finally(() => {
                        this.billingForm.busy = false;
                    });
            }
        },
        // Add methods to check for changes
        checkShippingFormChanged() {
            if (!this.shippingFormInitialValues) return false;

            return !_.isEqual(
                this.shippingForm.shipping_address,
                this.shippingFormInitialValues
            );
        },

        checkBillingFormChanged() {
            if (!this.billingFormInitialValues) return false;

            return !_.isEqual(
                this.billingForm.billing_address,
                this.billingFormInitialValues
            );
        },
        getCustomShippingProvider(init = false) {
            axios
                .get(`/web-api/cart/custom-shipping-provider`)
                .then((response) => {
                    //console.log("getCustomShippingProvider", response);
                    if (
                        response.data.custom_shipping_provider != null &&
                        response.data.custom_shipping_account_number != null
                    ) {
                        this.orderForm.custom_shipping_provider =
                            response.data.custom_shipping_provider;
                        this.orderForm.custom_shipping_account_number =
                            response.data.custom_shipping_account_number;
                        if (init) {
                            this.orderForm.custom_shipping = true;
                        }
                    }
                })
                .catch((e) => {
                    console.log(e);
                });
        },
        handleStripeChange(event) {
            this.complete = event.complete;
            // Note: Errors are now displayed by the StripeElements component itself
            // No need to add to form errors since we removed the form-feedback component
        },
        handleStripeError(error) {
            this.orderForm.finishProcessing();
            this.orderForm.errors.add("card_token", error.message);

            // Show user-friendly error notification
            this.$notify.error({
                title: "Payment Error",
                message:
                    "There was a problem processing your card. Please check your details and try again."
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.checkout-container {
    padding: 16px;
    width: 100%;
    margin: 0 auto;
}

.checkout-grid {
    display: grid;
    gap: 24px;

    @media (min-width: 768px) {
        grid-template-columns: 1fr 2fr;
        align-items: start;
    }
}

.checkout-summary {
    @media (min-width: 768px) {
        position: sticky;
        top: 24px;
    }
}

.summary-card,
.form-card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    padding: 16px;
    margin-top: 16px;
}

h2 {
    font-size: 24px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 24px;

    @media (max-width: 767px) {
        font-size: 20px;
        margin-bottom: 16px;
    }
}

// Form styling
.form-section {
    margin-bottom: 24px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 4px;

    h3 {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
        margin: 0 0 16px;
    }
}

.form-row {
    display: grid;
    gap: 16px;
    margin-bottom: 16px;

    @media (min-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
}

// Payment section
.payment-methods {
    display: grid;
    gap: 16px;
    margin: 16px 0;

    @media (min-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
}

// Shipping section
.shipping-options {
    margin: 16px 0;

    .el-radio-group {
        display: grid;
        gap: 12px;
    }
}

// Keep existing functionality styles
.stripe-card {
    padding: 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
}

.custom-shipping {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #ebeef5;
}

.actions {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.stripe-error {
    color: #f56c6c;
    font-size: 12px;
    margin-top: 8px;
    padding: 8px;
    background-color: #fef0f0;
    border-radius: 4px;
}

.checkout-form {
    max-width: 700px;

    .el-form-item {
        margin-bottom: 16px;
    }

    .bill-to {
        font-size: 14px;
        color: #606266;
        margin-top: 8px;
    }

    .submit-alert {
        margin-top: 16px;
    }
}

h4 {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 16px;
}

.el-radio-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ml-25 {
    margin-left: 25px;
}
</style>

<style>
.StripeElement {
    background-color: white;
    padding: 16px 12px;
    border-radius: 4px;
    border: 1px solid transparent;
    box-shadow: 0 1px 3px 0 #e6ebf1;
    -webkit-transition: box-shadow 150ms ease;
    transition: box-shadow 150ms ease;
    border: 1px solid transparent;
}

.StripeElement--complete {
    border-color: green !important;
}

.StripeElement--focus {
    box-shadow: 0 1px 3px 0 #cfd7df !important;
}

.StripeElement--invalid {
    border-color: #fa755a !important;
}

.StripeElement--webkit-autofill {
    background-color: #fefde5 !important;
}

/* Add to your existing styles */
.stripe-disabled {
    opacity: 0.5;
    pointer-events: none;
    background-color: #f5f7fa;
}

.payment-notice {
    margin-top: 8px;
    color: #e6a23c;
    font-size: 14px;
    padding: 8px;
    background-color: #fdf6ec;
    border-radius: 4px;
}
</style>
